import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Calendar, ArrowRight } from "lucide-react";
import { PUBLIC_MEDIA, BRAND } from "../../lib/constants";

/**
 * Iter42.4: Grand opening announcement banner.
 *
 * Sits between Hero and Showcase on the home page. Charcoal on cream,
 * bronze accents, walnut framing (matches the Iter40 luxury palette,
 * zero orange). Announces the October 2026 grand opening plus deep
 * links to the three landing pages the marketing team asked to surface.
 */
export default function ShowroomAnnouncement() {
  const bg = `${PUBLIC_MEDIA("theo-oppolia/public/catalog/theo_oppolia_whole_house/page_073.webp")}?w=1600`;

  return (
    <section
      className="relative overflow-hidden bg-[#1A1A1A] text-[#F5F2EB]"
      data-testid="showroom-announcement"
    >
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <img
          src={bg}
          srcSet={`${bg} 1600w, ${PUBLIC_MEDIA("theo-oppolia/public/catalog/theo_oppolia_whole_house/page_073.webp")}?w=800 800w`}
          sizes="100vw"
          alt=""
          width="1600"
          height="1200"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/60 via-[#1A1A1A]/40 to-[#1A1A1A]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="overline text-[#B08858] mb-5 inline-flex items-center gap-2"
          data-testid="showroom-announcement-eyebrow"
        >
          <Calendar size={12} />
          <span>Grand Opening, October 2026</span>
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] max-w-3xl"
          data-testid="showroom-announcement-headline"
        >
          Our Stoney Creek Showroom opens in{" "}
          <em className="not-italic text-[#B08858]">October</em>.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-6 max-w-2xl text-[#F5F2EB]/75 text-base md:text-lg leading-relaxed"
        >
          The exact grand opening date will be announced later this October.
          Soft opening visits and free 30 minute design consultations are open now
          at 1254 South Service Road, Units B22 and B23, Stoney Creek.
          Free onsite parking. Serving Hamilton, Burlington, Grimsby, Ancaster
          and the wider Golden Horseshoe.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-8 flex flex-wrap items-center gap-4"
          data-testid="showroom-announcement-ctas"
        >
          <Link
            to="/consultation"
            className="btn-sharp !bg-[#8B5E3C] hover:!bg-[#B08858] text-[#F5F2EB]"
            data-testid="showroom-announcement-cta-book"
          >
            Book Free Consultation
          </Link>
          <Link
            to="/blog/stoney-creek-showroom-hamilton"
            className="btn-sharp outline-light inline-flex items-center gap-2"
            data-testid="showroom-announcement-cta-blog"
          >
            <span>Read the Showroom Story</span>
            <ArrowRight size={14} />
          </Link>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=1254%20South%20Service%20Road%2C%20Units%20B22%2FB23%2C%20Stoney%20Creek%2C%20Ontario%20L8E%205C4"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-[#B08858] hover:text-[#F5F2EB] transition-colors"
            data-testid="showroom-announcement-cta-directions"
          >
            <MapPin size={12} />
            <span>Get Directions</span>
          </a>
        </motion.div>

        {/* Deep links to the three Cloud Stack landing articles */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-14 grid gap-4 md:grid-cols-3"
          data-testid="showroom-announcement-links"
        >
          {[
            { to: "/blog/stoney-creek-showroom-hamilton", eyebrow: "Showroom", title: "The Stoney Creek Showroom serving Hamilton and area" },
            { to: "/blog/tv-walls-and-built-ins-hamilton", eyebrow: "TV Walls and Built ins", title: "A living room that looks designed, not assembled" },
            { to: "/blog/custom-wardrobes-walk-in-closets-hamilton", eyebrow: "Custom Wardrobes", title: "Calm, organized mornings start with a better wardrobe" },
          ].map((card) => (
            <Link
              key={card.to}
              to={card.to}
              className="group block border border-[#F5F2EB]/15 hover:border-[#B08858] transition-colors p-5 bg-[#0A0A0A]/40 backdrop-blur-sm"
              data-testid={`showroom-announcement-link-${card.to.split("/").pop()}`}
            >
              <p className="overline text-[#B08858] mb-3">{card.eyebrow}</p>
              <h3 className="font-display text-lg md:text-xl leading-tight group-hover:text-[#B08858] transition-colors">
                {card.title}
              </h3>
              <span className="inline-flex items-center gap-1.5 mt-4 text-[10px] tracking-[0.18em] uppercase text-[#F5F2EB]/60 group-hover:text-[#F5F2EB]">
                Read the story <ArrowRight size={10} />
              </span>
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
